import { useEffect, useRef, useState } from 'react';
import { platform_header_mapper } from '../../utils/tableHeader';
import useCommonStore from 'store/useCommonStore';
import TableHeaderCell, {
	IHeaderCell,
	IHeaderNumberCell,
} from 'components/common/table/header/TableHeaderCell';
import { IHeader } from 'utils/tableHeader';
import useTableResizing from 'hooks/useTableResizing';
import { useLocation, useSearchParams } from 'react-router-dom';
import CoreSvg from 'shared/CoreSvg';

import { useTheme } from 'styled-components';
import Icon from 'shared/icons';
import { useQueryClient } from 'react-query';
import { IDiscoverDB } from 'type/db';

/** 헤더와 컬럼 넓이를 전역 상태로 세팅하고, 각 컬럼에 헤더 셀을 맵핑 시키는 객체를 반환하는 Hook */
const useTableHeader = () => {
	// store
	const {
		setHeader,
		column_width,
		setColumnWidth,
		platform,
		page,
		setPage,
		setCommonFilter,
	} = useCommonStore();
	const { common_filter } = useCommonStore((state) => state.filter);

	// router
	const location = useLocation();
	const [search_params] = useSearchParams();
	const page_query = search_params.get('page');

	// hover된 카테고리
	const [hovered_category, setHoveredCategory] = useState<number | null>(null);

	// ref
	const column_ref = useRef<HTMLDivElement[] | null[]>([]);
	const resizer_ref = useRef<HTMLDivElement[] | null[]>([]);

	const theme = useTheme();
	/** 헤더 세팅 */
	useEffect(() => {
		if (location.pathname === '/imms/match')
			setHeader(
				platform_header_mapper[platform.code ?? 'none'].filter(
					(item) => item.name !== 'follower_growth_list',
				),
			);
		else setHeader(platform_header_mapper[platform.code ?? 'none']);
	}, [platform_header_mapper, platform, location]);

	/** 컬럼 넒이 세팅 */
	useEffect(() => {
		setColumnWidth(
			platform_header_mapper[platform.code ?? 'none'].map((cell) => cell.width),
		);
	}, [platform_header_mapper, platform]);

	/** 쿼리스트링의 페이지 이동 시 전역 페이지 값 변경 */
	useEffect(() => {
		if (page_query) {
			setPage(+page_query);
		}
	}, [location, page, page_query]);

	/** 헤더 클릭 시 정렬 반영 */
	const clickOrder = (name: string) => {
		if (common_filter.order_by !== name) {
			setCommonFilter('order_by', name);
			setCommonFilter('order', 'desc');
		} else if (common_filter.order_by === name && common_filter.order === 'desc') {
			setCommonFilter('order_by', name);
			setCommonFilter('order', 'asc');
		} else if (common_filter.order_by === name && common_filter.order === 'asc') {
			setCommonFilter('order_by', 'random');
			setCommonFilter('order', 'desc');
		}
	};

	const queryClient = useQueryClient();

	/** 헤더 클릭 시 랭킹 정렬 반영 */
	const clickRankingOrder = (name: string) => {
		const ranking_data: any = queryClient.getQueryData(['rank_list', platform.code]);
		let sorted_list;

		if (common_filter.order_by !== name) {
			setCommonFilter('order_by', name);
			setCommonFilter('order', 'desc');
			sorted_list = ranking_data.results.sort((a: IDiscoverDB, b: IDiscoverDB) => {
				if (a[name] === null && b[name]) return 1;
				if (b[name] === null && a[name]) return -1;
				if (a[name] === null && b[name] === null) return 0;
				return b[name] - a[name];
			});
		} else if (common_filter.order_by === name && common_filter.order === 'desc') {
			setCommonFilter('order_by', name);
			setCommonFilter('order', 'asc');
			sorted_list = ranking_data.results.sort((a: IDiscoverDB, b: IDiscoverDB) => {
				if (a[name] === null && b[name]) return 1;
				if (b[name] === null && a[name]) return -1;
				if (a[name] === null && b[name] === null) return 0;
				else return a[name] - b[name];
			});
		} else if (common_filter.order_by === name && common_filter.order === 'asc') {
			setCommonFilter('order_by', 'random');
			setCommonFilter('order', 'desc');
			queryClient.invalidateQueries(['rank_list', platform.code]);
			return;
		}

		const sorted_data = { ...ranking_data, results: sorted_list };
		queryClient.setQueryData(['rank_list', platform.code], sorted_data);
	};

	/** 헤더 클릭 시 랭킹 정렬 반영 */
	const clickRankingCategoryOrder = () => {
		const ranking_data: any = queryClient.getQueryData(['rank_list', platform.code]);
		let sorted_list;

		if (common_filter.order_by !== 'category') {
			setCommonFilter('order_by', 'category');
			setCommonFilter('order', 'desc');
			sorted_list = ranking_data.results.sort((a: IDiscoverDB, b: IDiscoverDB) => {
				if (!a.category_1 && b.category_1) return 1;
				if (!b.category_1 && a.category_1) return -1;
				if (!a.category_1 && !b.category_1) return 0;
				return b.category_1?.localeCompare(a.category_1 as string);
			});
		} else if (common_filter.order_by === 'category' && common_filter.order === 'desc') {
			setCommonFilter('order_by', 'category');
			setCommonFilter('order', 'asc');
			sorted_list = ranking_data.results.sort((a: IDiscoverDB, b: IDiscoverDB) => {
				if (!a.category_1 && b.category_1) return 1;
				if (!b.category_1 && a.category_1) return -1;
				if (!a.category_1 && !b.category_1) return 0;
				return a.category_1?.localeCompare(b.category_1 as string);
			});
		} else if (common_filter.order_by === 'category' && common_filter.order === 'asc') {
			setCommonFilter('order_by', 'random');
			setCommonFilter('order', 'desc');
			queryClient.invalidateQueries(['rank_list', platform.code]);
			return;
		}

		const sorted_data = { ...ranking_data, results: sorted_list };
		queryClient.setQueryData(['rank_list', platform.code], sorted_data);
	};

	const setArrowIcon = (header_name: string, idx: number) => {
		// if (
		// 	location.pathname === '/imms/rank' ||
		// 	location.pathname === '/imms' ||
		// 	location.pathname === '/imms/'
		// )
		// 	return <> </>;
		if (common_filter.order_by !== header_name && hovered_category === idx)
			return (
				<CoreSvg _margin=" 0 -10px 0 4px" _fill={theme.colors.gray.gray20}>
					<Icon.DropArrowDown />
				</CoreSvg>
			);
		if (common_filter.order_by === header_name && common_filter.order === 'desc')
			return (
				<CoreSvg _margin=" 0 -10px 0 4px">
					<Icon.DropArrowDown />
				</CoreSvg>
			);
		if (common_filter.order_by === header_name && common_filter.order === 'asc')
			return (
				<CoreSvg _margin=" 0 -10px 0 4px">
					<Icon.DropArrowUp />
				</CoreSvg>
			);
		return <></>;
	};

	/** 테이블 리사이징 */
	useTableResizing(column_ref, resizer_ref);

	/** 헤더의 타입에 따라 JSX를 반환하는 객체 */
	const table_header_mapper: Record<
		string,
		(header: IHeader, idx: number, prev_header?: IHeader) => string | JSX.Element
	> = {
		none(header, idx, prev_header) {
			const width = column_width[idx] ?? header.width;
			const calc_width =
				prev_header === undefined || (prev_header && prev_header.type === 'none')
					? width
					: width - 8;
			const props: IHeaderCell = {
				_width: calc_width,
			};

			return (
				<TableHeaderCell.None key={idx} {...props}>
					{header.header}
				</TableHeaderCell.None>
			);
		},
		common(header, idx, prev_header) {
			const width = column_width[idx] ?? header.width;
			const calc_width =
				prev_header === undefined || (prev_header && prev_header.type === 'none')
					? width - 8
					: width - 16;

			const props: IHeaderCell = {
				_width: calc_width,
				_padding:
					prev_header && prev_header.type === 'none' ? '0 8px 0 16px' : '0 8px 0 8px',
				_ref: (e) => (column_ref.current[idx] = e),
				resizer_ref: (e) => (resizer_ref.current[idx] = e),
				_onMouseEnter: () =>
					header.name === 'category' && location.pathname !== '/imms/match'
						? setHoveredCategory(idx)
						: undefined,
				_onMouseLeave: () =>
					header.name === 'category' && location.pathname !== '/imms/match'
						? setHoveredCategory(null)
						: undefined,
				_onClick:
					header.name === 'category' && location.pathname !== '/imms/match'
						? () => clickRankingCategoryOrder()
						: undefined,
				tooltip_text: header?.tooltip_text,
			};

			return (
				<TableHeaderCell.Common key={idx} {...props}>
					{header.header}
					{header.name === 'category' && location.pathname !== '/imms/match'
						? setArrowIcon('category', idx)
						: ''}
				</TableHeaderCell.Common>
			);
		},
		number(header, idx, prev_header) {
			const width = column_width[idx] ?? header.width;
			const calc_width =
				prev_header === undefined || (prev_header && prev_header.type === 'none')
					? width - 8
					: width - 16;

			const props: IHeaderNumberCell = {
				_width: calc_width,
				_ref: (e) => (column_ref.current[idx] = e),
				resizer_ref: (e) => (resizer_ref.current[idx] = e),
				_onMouseEnter: () => setHoveredCategory(idx),
				_onMouseLeave: () => setHoveredCategory(null),
				_onClick:
					location.pathname === '/imms/match'
						? () => clickOrder(header.name)
						: () => clickRankingOrder(header.name),
				tooltip_text: header?.tooltip_text,
			};

			return (
				<TableHeaderCell.Number key={idx} {...props}>
					{header.header}
					{setArrowIcon(header.name, idx)}
				</TableHeaderCell.Number>
			);
		},
		etc(header, idx, prev_header) {
			const width = column_width[idx] ?? header.width;
			const calc_width =
				prev_header === undefined || (prev_header && prev_header.type === 'none')
					? width - 8
					: width - 16;

			const props: IHeaderCell = {
				_width: calc_width,
				_ref: (e) => (column_ref.current[idx] = e),
				resizer_ref: (e) => (resizer_ref.current[idx] = e),
				tooltip_text: header?.tooltip_text,
			};

			return (
				<TableHeaderCell.Etc key={idx} {...props}>
					{header.header}
				</TableHeaderCell.Etc>
			);
		},
	};

	return table_header_mapper;
};

export default useTableHeader;
